import { Controller } from 'stimulus';
import { MODAL_CLOSE_CONFIRMATION_SKIP_ONCE } from '../../javascript/lib/modal_close_confirmation_attributes';
import { t } from '../../javascript/lib/i18n';

const CONFIRM_ATTR = 'data-modal-close-confirmation';
const MESSAGE_ATTR = 'data-modal-close-confirmation-message';

/**
 * Shows a confirmation when a Bootstrap modal is closed. Attach to the page
 * (e.g. body) with modalId. Returns early unless the modal (or a descendant)
 * has data-modal-close-confirmation="true". Use "false" on the element to only
 * prompt after interaction—this controller then sets it to "true" on first
 * input or change (typing or changing a field). No wizard or content-type
 * detection.
 *
 * We accept the attribute on a descendant because the shared layout owns the
 * modal element; Turbo replaces only the frame content, so the attribute
 * lives on content inside the modal (e.g. .amba-wizard-modal-body).
 */
export default class extends Controller {
  static values = {
    modalId: { type: String, default: 'turbo-modal' },
    message: { type: String, default: '' },
  };

  connect() {
    this.modal = document.getElementById(this.modalIdValue);
    if (this.modal) {
      this.modal.addEventListener('hide.bs.modal', this.handleClose);
      this.modal.addEventListener('shown.bs.modal', this.handleShown);
      this.modal.addEventListener('hidden.bs.modal', this.handleHidden);
    }
  }

  disconnect() {
    if (this.modal) {
      this.modal.removeEventListener('hide.bs.modal', this.handleClose);
      this.modal.removeEventListener('shown.bs.modal', this.handleShown);
      this.modal.removeEventListener('hidden.bs.modal', this.handleHidden);
      this.removeInteractionListeners();
      this.stopObserving();
    }
  }

  handleShown = () => {
    const tryAttach = () => {
      const el = this.modal.querySelector(`[${CONFIRM_ATTR}]`);
      if (!el || el.getAttribute(CONFIRM_ATTR) === 'true') return false;

      const markInteracted = () => {
        this.modal.setAttribute(CONFIRM_ATTR, 'true');
        this.removeInteractionListeners();
        this.stopObserving();
      };

      this.interactionHandlers = [
        ['input', markInteracted, true],
        ['change', markInteracted, true],
      ];
      this.interactionHandlers.forEach(([ev, fn, cap]) =>
        this.modal.addEventListener(ev, fn, cap)
      );
      return true;
    };

    if (tryAttach()) return;

    this.observer = new MutationObserver(() => {
      if (tryAttach()) this.stopObserving();
    });
    this.observer.observe(this.modal, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [CONFIRM_ATTR],
    });
  };

  handleHidden = () => {
    if (this.modal) this.modal.removeAttribute(CONFIRM_ATTR);
    this.removeInteractionListeners();
    this.stopObserving();
  };

  removeInteractionListeners() {
    if (!this.interactionHandlers || !this.modal) return;
    this.interactionHandlers.forEach(([ev, fn, cap]) =>
      this.modal.removeEventListener(ev, fn, cap)
    );
    this.interactionHandlers = null;
  }

  stopObserving() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  handleClose = (event) => {
    const modal = event.target;
    const needsConfirm =
      modal.getAttribute(CONFIRM_ATTR) === 'true' ||
      modal.querySelector(`[${CONFIRM_ATTR}="true"]`);

    if (!needsConfirm) {
      return;
    }

    if (event.detail?.source === 'turbo') {
      return;
    }

    // Skip confirmation if this hide was triggered programmatically (e.g. Turbo
    // caching the page before navigation). Clear the attribute after checking.
    if (modal.hasAttribute(MODAL_CLOSE_CONFIRMATION_SKIP_ONCE)) {
      modal.removeAttribute(MODAL_CLOSE_CONFIRMATION_SKIP_ONCE);
      return;
    }

    const hasFormWithSubmitAction = modal.querySelector(
      'form[data-action*="turbo:submit-success"]'
    );
    if (hasFormWithSubmitAction) {
      return;
    }

    const messageEl = modal.querySelector(`[${MESSAGE_ATTR}]`);
    const message =
      messageEl?.getAttribute(MESSAGE_ATTR) ||
      this.messageValue ||
      t('js.bootstrap.modal_close_confirmation.leave_view');
    if (!confirm(message)) {
      event.preventDefault();
    }
  };
}
